import React, { forwardRef, useRef } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Info, Loader2, TriangleAlert, Upload } from "lucide-react";
import { cn } from "../lib/site";

// Buttons ----------------------------------------------------------------------

const BUTTON_VARIANTS = {
  primary: "bg-navy-700 text-white hover:bg-navy-800 focus-visible:outline-navy-700 shadow-sm",
  gold: "bg-gold-400 text-navy-900 hover:bg-gold-300 focus-visible:outline-gold-500 shadow-sm",
  secondary: "bg-white text-navy-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus-visible:outline-navy-700",
  ghost: "text-navy-700 hover:bg-navy-50 focus-visible:outline-navy-700",
  danger: "bg-red-600 text-white hover:bg-red-700 focus-visible:outline-red-600",
  whatsapp: "bg-[#25D366] text-white hover:bg-[#1ebe5a] focus-visible:outline-[#25D366] shadow-sm",
  light: "bg-white/10 text-white ring-1 ring-inset ring-white/30 hover:bg-white/20 focus-visible:outline-white",
};
const BUTTON_SIZES = {
  sm: "h-9 px-3 text-sm gap-1.5",
  md: "h-11 px-5 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2",
};

export const Button = forwardRef(function Button(
  { variant = "primary", size = "md", to, href, loading = false, icon: Icon, iconRight: IconRight, className, children, disabled, ...rest },
  ref
) {
  const classes = cn(
    "inline-flex items-center justify-center rounded-xl font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap",
    BUTTON_VARIANTS[variant],
    BUTTON_SIZES[size],
    className
  );
  const content = (
    <>
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : Icon ? <Icon className="h-4 w-4" aria-hidden /> : null}
      {children}
      {IconRight && !loading ? <IconRight className="h-4 w-4" aria-hidden /> : null}
    </>
  );
  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a ref={ref} href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button ref={ref} className={classes} disabled={disabled || loading} {...rest}>
      {content}
    </button>
  );
});

// Form fields ------------------------------------------------------------------

const FIELD_BASE =
  "block w-full rounded-xl border-0 bg-white px-3.5 py-2.5 text-[15px] text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-navy-600 disabled:bg-slate-50 disabled:text-slate-500";

export function Field({ label, hint, error, required, htmlFor, className, children }) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-sm font-semibold text-slate-800">
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="flex items-center gap-1 text-sm text-red-600">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef(function Input({ className, invalid, ...rest }, ref) {
  return <input ref={ref} className={cn(FIELD_BASE, invalid && "ring-red-500", className)} {...rest} />;
});

export const Textarea = forwardRef(function Textarea({ className, invalid, rows = 3, ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={cn(FIELD_BASE, invalid && "ring-red-500", className)} {...rest} />;
});

export const Select = forwardRef(function Select({ className, invalid, children, ...rest }, ref) {
  return (
    <select ref={ref} className={cn(FIELD_BASE, "pr-9", invalid && "ring-red-500", className)} {...rest}>
      {children}
    </select>
  );
});

/** Label + input in one, wired by id. */
export function TextField({ id, label, hint, error, required, as = "input", className, ...rest }) {
  const Control = as === "textarea" ? Textarea : as === "select" ? Select : Input;
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={id} className={className}>
      <Control id={id} name={id} invalid={!!error} required={required} aria-invalid={!!error} {...rest} />
    </Field>
  );
}

export function Checkbox({ id, label, description, className, ...rest }) {
  return (
    <label htmlFor={id} className={cn("flex cursor-pointer items-start gap-3", className)}>
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 h-5 w-5 rounded border-slate-300 text-navy-700 focus:ring-navy-600"
        {...rest}
      />
      <span className="text-sm text-slate-700">
        <span className="font-semibold text-slate-900">{label}</span>
        {description && <span className="block text-slate-500">{description}</span>}
      </span>
    </label>
  );
}

export function RadioCards({ name, value, onChange, options, columns = 2 }) {
  return (
    <div className={cn("grid gap-3", columns === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2")} role="radiogroup">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <label
            key={opt.value}
            className={cn(
              "relative flex cursor-pointer flex-col rounded-2xl border bg-white p-4 transition",
              active ? "border-navy-700 ring-2 ring-navy-700" : "border-slate-200 hover:border-slate-300"
            )}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={active}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            <span className="flex items-center gap-2 font-display text-base font-bold text-navy-800">
              {opt.icon}
              {opt.label}
            </span>
            {opt.description && <span className="mt-1 text-sm text-slate-600">{opt.description}</span>}
            {active && <CheckCircle2 className="absolute right-3 top-3 h-5 w-5 text-navy-700" aria-hidden />}
          </label>
        );
      })}
    </div>
  );
}

// Layout ---------------------------------------------------------------------------

export function Card({ title, subtitle, actions, className, bodyClassName, children }) {
  return (
    <section className={cn("rounded-2xl border border-slate-200 bg-white shadow-card", className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
          <div>
            {title && <h2 className="font-display text-base font-bold text-navy-800">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className={cn("px-5 py-5 sm:px-6", bodyClassName)}>{children}</div>
    </section>
  );
}

const ALERT_TONES = {
  info: ["bg-navy-50 text-navy-800 ring-navy-200", Info],
  success: ["bg-emerald-50 text-emerald-800 ring-emerald-200", CheckCircle2],
  warning: ["bg-amber-50 text-amber-900 ring-amber-200", TriangleAlert],
  error: ["bg-red-50 text-red-800 ring-red-200", AlertCircle],
};

export function Alert({ tone = "info", title, children, className, action }) {
  const [classes, Icon] = ALERT_TONES[tone];
  return (
    <div className={cn("flex gap-3 rounded-2xl p-4 ring-1 ring-inset", classes, className)} role={tone === "error" ? "alert" : "status"}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && "mt-1", "leading-relaxed")}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn("h-5 w-5 animate-spin text-navy-600", className)} aria-label="Loading" />;
}

export function PageLoader({ label = "Loading..." }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-slate-500">
      <Spinner className="h-8 w-8" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function ProgressBar({ value, className, tone = "navy" }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className={cn("h-2 w-full overflow-hidden rounded-full bg-slate-100", className)} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div
        className={cn("h-full rounded-full transition-all duration-500", pct === 100 ? "bg-emerald-500" : tone === "gold" ? "bg-gold-400" : "bg-navy-600")}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

const STATUS_STYLES = {
  submitted: "bg-sky-50 text-sky-800 ring-sky-200",
  under_review: "bg-violet-50 text-violet-800 ring-violet-200",
  interview: "bg-amber-50 text-amber-900 ring-amber-200",
  approved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  waitlisted: "bg-orange-50 text-orange-800 ring-orange-200",
  rejected: "bg-red-50 text-red-800 ring-red-200",
  enrolled: "bg-navy-700 text-white ring-navy-700",
  withdrawn: "bg-slate-100 text-slate-600 ring-slate-200",
};

export const STATUS_LABELS = {
  submitted: "Submitted",
  under_review: "Under review",
  interview: "Interview scheduled",
  approved: "Offer made",
  waitlisted: "Waitlisted",
  rejected: "Not successful",
  enrolled: "Enrolled",
  withdrawn: "Withdrawn",
};

export function StatusBadge({ status, className }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset",
        STATUS_STYLES[status] || "bg-slate-100 text-slate-700 ring-slate-200",
        className
      )}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, children, action }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-12 text-center">
      {Icon && (
        <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-navy-50 text-navy-700">
          <Icon className="h-7 w-7" aria-hidden />
        </div>
      )}
      <h3 className="font-display text-lg font-bold text-navy-800">{title}</h3>
      {children && <div className="mt-2 max-w-md text-sm text-slate-600">{children}</div>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

/** A button that opens the file picker. */
export function FilePicker({ accept, onFile, children = "Choose file", variant = "secondary", size = "sm", loading, icon = Upload, disabled }) {
  const input = useRef(null);
  return (
    <>
      <Button type="button" variant={variant} size={size} icon={icon} loading={loading} disabled={disabled} onClick={() => input.current?.click()}>
        {children}
      </Button>
      <input
        ref={input}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onFile(file);
        }}
      />
    </>
  );
}

export function Stepper({ steps, current, onStepClick }) {
  return (
    <ol className="flex w-full items-center gap-2 overflow-x-auto pb-1" aria-label="Progress">
      {steps.map((step, i) => {
        const state = i < current ? "done" : i === current ? "current" : "todo";
        return (
          <li key={step} className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              disabled={state === "todo"}
              onClick={() => state === "done" && onStepClick?.(i)}
              className={cn(
                "flex min-w-0 items-center gap-2 rounded-full py-1 pr-3 text-left text-sm font-semibold",
                state === "todo" ? "cursor-default text-slate-400" : "text-navy-800",
                state === "done" && "hover:text-navy-600"
              )}
              aria-current={state === "current" ? "step" : undefined}
            >
              <span
                className={cn(
                  "grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold",
                  state === "done" && "bg-navy-700 text-white",
                  state === "current" && "bg-gold-400 text-navy-900 ring-4 ring-gold-100",
                  state === "todo" && "bg-slate-100 text-slate-500"
                )}
              >
                {state === "done" ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : i + 1}
              </span>
              <span className="hidden truncate lg:inline">{step}</span>
            </button>
            {i < steps.length - 1 && <span className={cn("h-0.5 flex-1 rounded", i < current ? "bg-navy-700" : "bg-slate-200")} />}
          </li>
        );
      })}
    </ol>
  );
}
